import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, CheckCircle, XCircle, AlertTriangle,
  Rocket, Shield, Pause, Play, RefreshCw,
} from 'lucide-react';
import api from '../../lib/api';

export default function OnboardingReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [readiness, setReadiness] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [readinessRes, detailRes] = await Promise.all([
        api.get(`/client-onboarding/${id}/readiness`),
        api.get(`/client-onboarding/${id}`),
      ]);
      setReadiness(readinessRes.data);
      setData(detailRes.data);
    } catch (e) {
      toast.error('Failed to load review data');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [confirmAction, setConfirmAction] = useState(null);

  const handleActivate = async () => {
    setConfirmAction('activate');
  };

  const handlePause = async () => {
    setConfirmAction('pause');
  };

  const handleResume = async () => {
    setConfirmAction('resume');
  };

  const executeConfirmedAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    setActionLoading(action);
    try {
      if (action === 'activate') {
        const res = await api.post(`/client-onboarding/${id}/pilot/activate`);
        toast.success(res.data.message || 'Pilot activated');
      } else if (action === 'pause') {
        await api.post(`/client-onboarding/${id}/pilot/pause`);
        toast.success('Pilot paused');
      } else if (action === 'resume') {
        await api.post(`/client-onboarding/${id}/pilot/resume`);
        toast.success('Pilot resumed');
      }
      fetchData();
    } catch (e) {
      const err = e.response?.data?.detail?.error || e.response?.data?.error;
      toast.error(err?.message || `Failed to ${action}`);
      if (err?.details?.blockingIssues) {
        err.details.blockingIssues.forEach(b => toast.error(b, { duration: 5000 }));
      }
    }
    setActionLoading('');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="onboarding-review-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/onboarding/${id}`)} data-testid="back-to-detail">
          <ArrowLeft className="h-4 w-4 mr-1" />Back to Detail
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Readiness Review</h1>
          <p className="text-sm text-muted-foreground">{data?.onboardingName} ({data?.onboardingCode})</p>
        </div>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={fetchData} data-testid="refresh-review">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {readiness && (
        <>
          {/* Readiness Score */}
          <Card className={readiness.eligible ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                {readiness.eligible ? (
                  <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-emerald-600" />
                  </div>
                ) : (
                  <div className="h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle className="h-8 w-8 text-amber-600" />
                  </div>
                )}
                <div>
                  <p className="text-xl font-bold">{readiness.eligible ? 'Ready for Pilot' : 'Not Ready'}</p>
                  <p className="text-sm text-muted-foreground">
                    Checklist: {readiness.checklistProgress}% | Status: {readiness.status}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Blocking Issues */}
          {readiness.blockingIssues?.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-red-700">
                  <XCircle className="h-4 w-4" />Blocking Issues ({readiness.blockingIssues.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {readiness.blockingIssues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                      <span>{issue}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Warnings */}
          {readiness.warnings?.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />Warnings ({readiness.warnings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {readiness.warnings.map((w, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Checklist Summary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" />Checklist Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all"
                       style={{ width: `${readiness.checklistProgress}%` }} />
                </div>
                <span className="text-sm font-bold">{readiness.checklistProgress}%</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {readiness.checklist?.completed}/{readiness.checklist?.total} items complete | {readiness.importsReady} validated imports
              </p>
            </CardContent>
          </Card>

          {/* Pilot Info */}
          {data?.pilotConfig && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Rocket className="h-4 w-4" />Pilot Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Pilot Enabled</span><span>{data.pilotConfig.pilotEnabled ? 'Yes' : 'No'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Target Stage</span><span>{data.pilotConfig.targetPilotStage || 'Not set'}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Feature Flags</span><span>{data.pilotConfig.featureFlagsRequested?.join(', ') || 'None'}</span></div>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* Actions */}
          <div className="flex gap-3 justify-end flex-wrap">
            {readiness.eligible && ['pilot_ready', 'review_pending'].includes(data?.status) && (
              <Button className="bg-green-600 hover:bg-green-700" onClick={handleActivate}
                      disabled={actionLoading === 'activate'} data-testid="activate-pilot-btn">
                {actionLoading === 'activate' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Rocket className="h-4 w-4 mr-1" />}
                Activate Pilot
              </Button>
            )}
            {['pilot_activated', 'pilot_live'].includes(data?.status) && (
              <Button variant="outline" className="text-orange-600 border-orange-300" onClick={handlePause}
                      disabled={actionLoading === 'pause'} data-testid="pause-pilot-btn">
                <Pause className="h-4 w-4 mr-1" />Pause Pilot
              </Button>
            )}
            {data?.status === 'pilot_paused' && (
              <Button variant="outline" className="text-green-600 border-green-300" onClick={handleResume}
                      disabled={actionLoading === 'resume'} data-testid="resume-pilot-btn">
                <Play className="h-4 w-4 mr-1" />Resume Pilot
              </Button>
            )}
          </div>

          {/* Confirmation Dialog */}
          <AlertDialog open={!!confirmAction} onOpenChange={open => !open && setConfirmAction(null)}>
            <AlertDialogContent data-testid="pilot-confirm-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirmAction === 'activate' ? 'Activate Pilot Rollout' : confirmAction === 'pause' ? 'Pause Pilot' : 'Resume Pilot'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirmAction === 'activate'
                    ? `This will activate the pilot at stage "${data?.pilotConfig?.targetPilotStage || 'stage_1'}". Feature flags and kill switches will be adjusted via Day 24 rollout controls. This action is audited.`
                    : confirmAction === 'pause'
                    ? 'This will pause the active pilot. No rollout changes will be reverted until resumed.'
                    : 'This will resume the paused pilot and restore its previous rollout state.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="pilot-confirm-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={executeConfirmedAction} data-testid="pilot-confirm-proceed">
                  {confirmAction === 'activate' ? 'Activate Pilot' : confirmAction === 'pause' ? 'Pause Pilot' : 'Resume Pilot'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
